/**
 * useSafeRollouts (J2-I2) — list-on-mount + evaluate/rollback actions for the
 * Safe Rollout panel. Imperative-first: every mutation re-fetches the list
 * explicitly (`await action(); await refetch()`), no listeners.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { launchReadinessClient } from '../api/launchReadinessClient';
import { sortSafeRolloutsNewestFirst } from '../utils/safeRollout';
import type { SafeRolloutRecord } from '../types/safeRollout.types';

export interface UseSafeRolloutsResult {
  rollouts: SafeRolloutRecord[];
  /** True only for the initial load (no data yet). */
  loading: boolean;
  error: Error | null;
  /** Rollout id with an evaluate/rollback request in flight, if any. */
  busyId: string | null;
  /** Error from the last evaluate/rollback action (list errors go to `error`). */
  actionError: Error | null;
  refetch: () => Promise<void>;
  evaluate: (safeRolloutId: string) => Promise<void>;
  rollback: (safeRolloutId: string) => Promise<void>;
}

export function useSafeRollouts(projectId: string | undefined): UseSafeRolloutsResult {
  const [rollouts, setRollouts] = useState<SafeRolloutRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setError(null);
    try {
      const fresh = await launchReadinessClient.listSafeRollouts(projectId);
      if (seq !== requestSeq.current) return; // superseded by a newer request
      setRollouts(sortSafeRolloutsNewestFirst(fresh));
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    requestSeq.current += 1; // invalidate in-flight requests from a previous project
    setRollouts([]);
    setLoading(true);
    setError(null);
    setActionError(null);
    void refetch();
  }, [refetch]);

  const runAction = useCallback(
    async (safeRolloutId: string, action: (projectId: string, id: string) => Promise<unknown>) => {
      if (!projectId || busyId) return;
      setBusyId(safeRolloutId);
      setActionError(null);
      try {
        await action(projectId, safeRolloutId);
      } catch (err) {
        setActionError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setBusyId(null);
      }
      // Explicit refresh even after a failed action — a rollback that errored
      // late (e.g. probe failure) may still have closed the record.
      await refetch();
    },
    [projectId, busyId, refetch],
  );

  const evaluate = useCallback(
    (safeRolloutId: string) =>
      runAction(safeRolloutId, (pid, id) => launchReadinessClient.evaluateSafeRollout(pid, id)),
    [runAction],
  );

  const rollback = useCallback(
    (safeRolloutId: string) =>
      runAction(safeRolloutId, (pid, id) => launchReadinessClient.rollbackSafeRollout(pid, id)),
    [runAction],
  );

  return { rollouts, loading, error, busyId, actionError, refetch, evaluate, rollback };
}
